import React, { useRef, useState } from "react";
import { ImagePlus, Loader2, Tag, X } from "lucide-react";
import { toast } from "sonner";
import { AdminModal } from "./AdminModal";
import { CodeCard } from "../../components/CodeCard";
import type { Product } from "../../types";
import { compressImageToWebpDataUrl } from "../../utils/compressImageToWebp";

export type ProductFormValues = {
  name: string;
  category: string;
  description: string;
  price: string;
  /** data URL or https URL; empty clears on update when clearImage */
  images?: string[];
  clearImage?: boolean;
};

const emptyForm: ProductFormValues = {
  name: "",
  category: "",
  description: "",
  price: "",
  images: undefined,
  clearImage: false,
};

function fromProduct(product: Product): ProductFormValues {
  return {
    name: product.name ?? "",
    category: product.category ?? "",
    description: product.description ?? "",
    price: product.price ?? "",
    images: product.images?.length ? [...product.images] : undefined,
    clearImage: false,
  };
}

export function ProductEditorModal({
  mode,
  initial,
  onClose,
  onSave,
}: {
  mode: "create" | "view" | "edit";
  initial?: Product;
  onClose: () => void;
  onSave: (data: ProductFormValues) => Promise<void>;
}) {
  const [form, setForm] = useState<ProductFormValues>(initial ? fromProduct(initial) : emptyForm);
  const [saving, setSaving] = useState(false);
  const [compressing, setCompressing] = useState(false);
  const [editing, setEditing] = useState(mode !== "view");
  const fileRef = useRef<HTMLInputElement>(null);

  const preview = form.clearImage ? undefined : form.images?.[0] || initial?.images?.[0];

  const title =
    mode === "create"
      ? "Добавить товар"
      : editing
        ? "Редактировать товар"
        : "Товар на выставке";

  const fields: Array<{
    key: keyof Pick<ProductFormValues, "name" | "category" | "price" | "description">;
    label: string;
    placeholder: string;
    multiline?: boolean;
  }> = [
    { key: "name", label: "Название*", placeholder: "Натуральные краски для дерева" },
    { key: "category", label: "Категория", placeholder: "Лакокрасочные материалы" },
    { key: "price", label: "Цена / условия", placeholder: "от 500 ₽ / м²" },
    { key: "description", label: "Описание", placeholder: "Кратко для визитки и QR", multiline: true },
  ];

  const handlePickImage = async (file: File | null) => {
    if (!file) return;
    if (!file.type.startsWith("image/") && !/\.(heic|heif)$/i.test(file.name)) {
      toast.error("Выберите файл изображения (JPG, PNG, WebP…)");
      return;
    }
    setCompressing(true);
    try {
      const dataUrl = await compressImageToWebpDataUrl(file);
      setForm((prev) => ({ ...prev, images: [dataUrl], clearImage: false }));
    } catch (err) {
      const code = err instanceof Error ? err.message : "";
      if (code === "source_too_large") {
        toast.error("Файл слишком большой для обработки в браузере (макс. ~40 МБ).");
      } else {
        toast.error("Не удалось обработать изображение. Попробуйте другой файл.");
      }
    } finally {
      setCompressing(false);
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error("Укажите название товара");
      return;
    }
    setSaving(true);
    try {
      await onSave({
        name: form.name.trim(),
        category: form.category.trim(),
        description: form.description.trim(),
        price: form.price.trim(),
        images: form.clearImage ? [] : form.images,
        clearImage: form.clearImage,
      });
      onClose();
    } catch {
      toast.error("Не удалось сохранить товар");
    } finally {
      setSaving(false);
    }
  };

  return (
    <AdminModal
      title={title}
      onClose={onClose}
      maxWidthClassName="max-w-lg"
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-lg text-[13px] text-gray-600 hover:bg-gray-100"
          >
            Закрыть
          </button>
          {mode === "create" || editing ? (
            <button
              type="button"
              disabled={saving || compressing}
              onClick={() => void handleSave()}
              className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-[13px] disabled:opacity-60"
            >
              {saving ? "Сохранение…" : "Сохранить"}
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-[13px]"
            >
              Редактировать
            </button>
          )}
        </>
      }
    >
      <div className="flex flex-col items-center gap-2">
        {preview ? (
          <div className="relative">
            <img src={preview} alt="" className="w-24 h-24 rounded-xl object-cover" />
            {(mode === "create" || editing) && (
              <button
                type="button"
                onClick={() => setForm((prev) => ({ ...prev, images: undefined, clearImage: true }))}
                className="absolute -top-2 -right-2 p-1 rounded-full bg-white border border-gray-200 text-gray-500 shadow-sm"
                title="Убрать фото"
              >
                <X size={12} />
              </button>
            )}
          </div>
        ) : (
          <div className="w-24 h-24 rounded-xl bg-indigo-50 flex items-center justify-center">
            <Tag size={28} className="text-indigo-400" />
          </div>
        )}
        {(mode === "create" || editing) && (
          <>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,.heic,.heif"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0] ?? null;
                e.target.value = "";
                void handlePickImage(file);
              }}
            />
            <button
              type="button"
              disabled={compressing}
              onClick={() => fileRef.current?.click()}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-indigo-200 bg-indigo-50 text-indigo-700 text-[12px] disabled:opacity-60"
            >
              {compressing ? <Loader2 size={14} className="animate-spin" /> : <ImagePlus size={14} />}
              {compressing ? "Сжимаем…" : preview ? "Заменить фото" : "Добавить фото"}
            </button>
            <p className="text-[11px] text-gray-400 text-center max-w-[260px] leading-snug">
              Любой размер — фото автоматически сожмётся в WebP перед сохранением
            </p>
          </>
        )}
      </div>

      {mode === "create" || editing ? (
        <>
          {fields.map((f) =>
            f.multiline ? (
              <div key={f.key}>
                <label className="text-[11px] text-gray-500 mb-1 block">{f.label}</label>
                <textarea
                  value={form[f.key]}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  placeholder={f.placeholder}
                  rows={3}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] outline-none focus:border-indigo-400 resize-none"
                />
              </div>
            ) : (
              <div key={f.key}>
                <label className="text-[11px] text-gray-500 mb-1 block">{f.label}</label>
                <input
                  value={form[f.key]}
                  onChange={(e) => setForm({ ...form, [f.key]: e.target.value })}
                  placeholder={f.placeholder}
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-[13px] outline-none focus:border-indigo-400"
                />
              </div>
            ),
          )}
        </>
      ) : (
        <div className="space-y-2">
          <div>
            <div className="text-[15px] text-gray-900">{form.name || "—"}</div>
            <div className="text-[13px] text-indigo-600">{form.category || "Категория не указана"}</div>
          </div>
          {form.price ? (
            <div className="text-[13px] text-emerald-700 bg-emerald-50 inline-flex px-2 py-0.5 rounded-full">
              {form.price}
            </div>
          ) : null}
          {form.description ? (
            <p className="text-[12px] text-gray-500 leading-snug">{form.description}</p>
          ) : null}
        </div>
      )}

      {initial?.qrCode ? (
        <CodeCard
          title="QR товара"
          subtitle="Разместите на образце на стенде. Посетитель отсканирует и сохранит карточку товара."
          qrValue={initial.qrCode}
          badge="Товар"
          badgeColor="bg-violet-100 text-violet-700"
          downloadName={`${initial.name}-product`}
        />
      ) : mode === "create" ? (
        <p className="text-[12px] text-sky-700 bg-sky-50 rounded-xl p-3">
          QR товара создастся автоматически после сохранения.
        </p>
      ) : null}
    </AdminModal>
  );
}
